'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Send, CheckCircle2, Calendar, Zap, Building2,
    Briefcase, Mail, Phone, FileText, Clock, MessageSquare, X,
} from 'lucide-react';
import { Smartphone, Hash, Wallet } from '@/lib/icons';
import { EASE_CUSTOM } from '@/lib/animations';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xdawnkdn';

const CARRIERS = [
    {
        id: 'premium',
        label: 'Premium',
        sub: 'Quiero hablar primero por videollamada',
        icon: Calendar,
    },
    {
        id: 'express',
        label: 'Express',
        sub: 'Tengo claro lo que necesito · presupuesto por email',
        icon: Zap,
    },
    {
        id: 'asincrono',
        label: 'Asíncrono',
        sub: 'Sin reuniones · vídeo explicativo + PDF por WhatsApp',
        icon: Smartphone,
    },
];

const SECTORS = [
    'Hostelería', 'Construcción / Reformas', 'Gestoría', 'Clínicas',
    'Retail / Comercio', 'Educación', 'E-commerce', 'Peluquería / Estética',
    'Inmobiliaria', 'Otro',
];

const SIZES = [
    '1-5 empleados', '5-15 empleados', '15-50 empleados', '+50 empleados',
];

const PROJECT_TYPES = [
    'Web', 'App / Software a medida', 'CRM', 'Automatizaciones',
    'Dashboard / BI', 'Integración con otras herramientas', 'Otro',
];

const PLAZOS = [
    'Urgente (< 1 mes)', '1-3 meses', '3-6 meses', 'Sin prisa',
];

const INVERSIONES = [
    '< 2.000€', '2.000€ - 5.000€', '5.000€ - 10.000€',
    '10.000€ - 25.000€', '+ 25.000€', 'No lo sé, asesóradme',
];

export default function ContactForm() {
    const [carrier, setCarrier] = useState('premium');
    const [form, setForm] = useState({
        nombre: '', empresa: '', email: '', whatsapp: '',
        sector: '', tamano: '',
        descripcion: '', plazo: '', inversion: '',
        rgpd: false, comunicaciones: false,
    });
    const [tipoProyecto, setTipoProyecto] = useState<string[]>([]);
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [prefillNote, setPrefillNote] = useState<string | null>(null);

    useEffect(() => {
        const applyPrefill = (proceso: string) => {
            setForm(prev => ({
                ...prev,
                descripcion: `Quiero automatizar el proceso: ${proceso}\n\nNuestra situación actual: `,
            }));
            setTipoProyecto(prev => prev.includes('Automatizaciones') ? prev : [...prev, 'Automatizaciones']);
            setPrefillNote(proceso);
        };

        // Pickup from sessionStorage (navigation from /mapa-automatizacion)
        const stored = sessionStorage.getItem('prefill_proceso');
        if (stored) {
            applyPrefill(stored);
            sessionStorage.removeItem('prefill_proceso');
        }

        // Real-time event (same-page click from AutomationMap)
        const handler = (e: Event) => {
            const proceso = (e as CustomEvent<{ proceso: string }>).detail.proceso;
            applyPrefill(proceso);
        };
        window.addEventListener('prefill-contacto', handler);
        return () => window.removeEventListener('prefill-contacto', handler);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setForm(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const toggleProjectType = (type: string) => {
        setTipoProyecto(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!form.rgpd) return;
        setStatus('loading');
        try {
            const carrierLabel = CARRIERS.find(c => c.id === carrier)?.label || 'Premium';
            const res = await fetch(FORMSPREE_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    _subject: `[${carrierLabel.toUpperCase()}] ${form.nombre} · ${form.sector || 'Sin sector'} · ${form.empresa || 'Sin empresa'}`,
                    carril: carrierLabel,
                    nombre: form.nombre,
                    empresa: form.empresa,
                    email: form.email,
                    whatsapp: form.whatsapp || 'No facilitado',
                    sector: form.sector || 'No especificado',
                    tamano_empresa: form.tamano || 'No especificado',
                    tipo_proyecto: tipoProyecto.length > 0 ? tipoProyecto.join(', ') : 'No especificado',
                    descripcion: form.descripcion,
                    plazo_deseado: form.plazo || 'No especificado',
                    inversion_orientativa: form.inversion || 'No especificado',
                    acepta_comunicaciones: form.comunicaciones ? 'Sí' : 'No',
                    fecha_envio: new Date().toISOString(),
                }),
            });
            if (res.ok) setStatus('success');
            else setStatus('error');
        } catch {
            setStatus('error');
        }
    };

    const activeCarrier = CARRIERS.find(c => c.id === carrier)!;
    const ActiveIcon = activeCarrier.icon;

    const successMessage =
        carrier === 'premium' ? 'Te enviamos enlace de Calendly para agendar la videollamada en menos de 24h.' :
        carrier === 'express' ? 'Recibirás propuesta detallada por email en menos de 24h.' :
        'En 24h recibirás un vídeo explicativo por WhatsApp con la estrategia y presupuesto.';

    return (
        <section id="contacto" className="py-20 md:py-28 relative z-10">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.9, ease: EASE_CUSTOM }}
                    className="text-center mb-10"
                >
                    <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-4">
                        Cuéntanos qué necesitas
                    </div>
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Diagnóstico previo gratuito.
                        <br />
                        <span className="text-slate-400 font-light">Propuesta en 24-48h.</span>
                    </h2>
                    <p className="text-slate-500 font-light max-w-xl mx-auto">
                        Cada propuesta se diseña tras analizar tu situación. Tú eliges cómo prefieres recibirla.
                    </p>
                </motion.div>

                {prefillNote && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2.5 bg-blue-50 border border-blue-100 text-blue-700 rounded-xl px-4 py-2.5 mb-2 text-sm"
                    >
                        <Zap size={14} className="shrink-0 text-blue-500" />
                        <span className="flex-1 leading-snug">
                            Pre-rellenado desde el mapa · <strong>{prefillNote}</strong>
                        </span>
                        <button
                            type="button"
                            onClick={() => setPrefillNote(null)}
                            className="shrink-0 text-blue-400 hover:text-blue-600 transition-colors"
                            aria-label="Cerrar"
                        >
                            <X size={14} />
                        </button>
                    </motion.div>
                )}

                {status === 'success' ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass-card rounded-3xl p-10 text-center"
                    >
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 mb-5">
                            <CheckCircle2 className="w-7 h-7 text-blue-600" />
                        </div>
                        <h3 className="text-2xl font-black tracking-tight text-slate-900 mb-3">
                            Solicitud recibida
                        </h3>
                        <p className="text-slate-600 mb-2">
                            Gracias <span className="font-semibold text-slate-900">{form.nombre}</span>.
                        </p>
                        <p className="text-slate-500 max-w-md mx-auto">
                            {successMessage}
                        </p>
                        <p className="mt-5 text-xs font-mono tracking-widest text-slate-400 uppercase">
                            Respuesta en {form.email}
                        </p>
                    </motion.div>
                ) : (
                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE_CUSTOM }}
                        onSubmit={handleSubmit}
                        className="glass-card rounded-3xl p-6 sm:p-8 md:p-10 flex flex-col gap-8"
                    >

                        {/* ── BLOQUE 1 · Tu negocio ── */}
                        <div className="flex flex-col gap-5">
                            <div className="flex items-center gap-2 text-xs font-mono tracking-[0.25em] text-slate-400 uppercase">
                                <span className="w-6 h-px bg-slate-300" />
                                01 · Tu negocio
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field icon={Briefcase} label="Nombre completo" name="nombre" type="text" required value={form.nombre} onChange={handleChange} placeholder="Tu nombre" />
                                <Field icon={Building2} label="Empresa" name="empresa" type="text" required value={form.empresa} onChange={handleChange} placeholder="Nombre de tu empresa" />
                                <Field icon={Mail} label="Email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="tu@empresa.com" />
                                <Field icon={Phone} label="WhatsApp" name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} placeholder="+34 ··· ··· ···" sub="opcional · recomendado para asíncrono" />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <SelectField icon={Hash} label="Sector" name="sector" required value={form.sector} onChange={handleChange} options={SECTORS} placeholder="Selecciona tu sector" />
                                <SelectField icon={Briefcase} label="Tamaño empresa" name="tamano" value={form.tamano} onChange={handleChange} options={SIZES} placeholder="¿Cuántos sois?" />
                            </div>
                        </div>

                        {/* ── BLOQUE 2 · Qué necesitas ── */}
                        <div className="flex flex-col gap-5">
                            <div className="flex items-center gap-2 text-xs font-mono tracking-[0.25em] text-slate-400 uppercase">
                                <span className="w-6 h-px bg-slate-300" />
                                02 · Qué necesitas
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-mono tracking-widest text-slate-500 uppercase flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5" />
                                    Tipo de proyecto <span className="text-slate-400 normal-case font-sans tracking-normal">(selección múltiple)</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {PROJECT_TYPES.map(t => {
                                        const active = tipoProyecto.includes(t);
                                        return (
                                            <button
                                                type="button"
                                                key={t}
                                                onClick={() => toggleProjectType(t)}
                                                className={`px-3.5 py-2 rounded-xl text-xs font-medium border transition-all active:scale-95 ${active ? 'bg-blue-600 text-white border-blue-600 shadow-[0_2px_8px_rgba(37,99,235,0.25)]' : 'bg-white/60 text-slate-600 border-slate-200/60 hover:bg-white hover:border-slate-300'}`}
                                            >
                                                {t}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-mono tracking-widest text-slate-500 uppercase flex items-center gap-1.5">
                                    <MessageSquare className="w-3.5 h-3.5" />
                                    ¿Qué problema quieres resolver?
                                </label>
                                <textarea
                                    name="descripcion"
                                    placeholder="Cuéntanos brevemente la situación: qué hacéis, qué no funciona, qué te gustaría conseguir..."
                                    value={form.descripcion}
                                    onChange={handleChange}
                                    rows={4}
                                    required
                                    className="w-full bg-white/70 border border-slate-200/60 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 transition-all text-sm resize-none"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <SelectField icon={Clock} label="Plazo deseado" name="plazo" value={form.plazo} onChange={handleChange} options={PLAZOS} placeholder="¿Cuándo lo necesitas?" />
                                <SelectField icon={Wallet} label="Inversión orientativa" name="inversion" value={form.inversion} onChange={handleChange} options={INVERSIONES} placeholder="Rango aproximado" />
                            </div>
                        </div>

                        {/* ── BLOQUE 3 · Cómo prefieres trabajar (CARRIL) ── */}
                        <div className="flex flex-col gap-5">
                            <div className="flex items-center gap-2 text-xs font-mono tracking-[0.25em] text-slate-400 uppercase">
                                <span className="w-6 h-px bg-slate-300" />
                                03 · Cómo prefieres trabajar
                            </div>

                            <div className="grid grid-cols-1 gap-2">
                                {CARRIERS.map(c => {
                                    const Icon = c.icon;
                                    const active = carrier === c.id;
                                    return (
                                        <button
                                            type="button"
                                            key={c.id}
                                            onClick={() => setCarrier(c.id)}
                                            className={`flex items-center gap-3.5 p-4 rounded-2xl border text-left transition-all ${active ? 'bg-blue-50/70 border-blue-300 ring-2 ring-blue-200/50' : 'bg-white/60 border-slate-200/60 hover:bg-white hover:border-slate-300'}`}
                                        >
                                            <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border ${active ? 'bg-blue-600 border-blue-600' : 'bg-slate-50 border-slate-200/60'}`}>
                                                <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-slate-500'}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className={`font-bold text-sm ${active ? 'text-slate-900' : 'text-slate-800'}`}>{c.label}</div>
                                                <div className="text-xs text-slate-500 leading-relaxed">{c.sub}</div>
                                            </div>
                                            <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? 'border-blue-600' : 'border-slate-300'}`}>
                                                {active && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── BLOQUE 4 · Privacidad ── */}
                        <div className="flex flex-col gap-3 pt-2 border-t border-slate-200/60">
                            <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-600">
                                <input
                                    type="checkbox"
                                    name="rgpd"
                                    checked={form.rgpd}
                                    onChange={handleChange}
                                    required
                                    className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer"
                                />
                                <span className="leading-relaxed">
                                    He leído y acepto la <a href="/privacidad" target="_blank" className="text-blue-600 underline font-medium">Política de Privacidad</a> conforme al RGPD <span className="text-red-500">*</span>
                                </span>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-600">
                                <input
                                    type="checkbox"
                                    name="comunicaciones"
                                    checked={form.comunicaciones}
                                    onChange={handleChange}
                                    className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer"
                                />
                                <span className="leading-relaxed">
                                    Acepto recibir mi propuesta y comunicaciones relacionadas por email y/o WhatsApp
                                </span>
                            </label>
                        </div>

                        {status === 'error' && (
                            <p className="text-red-600 text-sm text-center font-medium">
                                Error al enviar. Escríbenos directamente a <a href="mailto:info@innovatheratech.com" className="underline">info@innovatheratech.com</a>
                            </p>
                        )}

                        <motion.button
                            type="submit"
                            disabled={status === 'loading' || !form.rgpd}
                            whileHover={{ scale: form.rgpd ? 1.01 : 1 }}
                            whileTap={{ scale: form.rgpd ? 0.99 : 1 }}
                            className={`w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2.5 transition-all ${form.rgpd ? 'bg-blue-600 hover:bg-blue-700 shadow-[0_4px_16px_rgba(37,99,235,0.3)]' : 'bg-slate-300 cursor-not-allowed'}`}
                        >
                            {status === 'loading' ? (
                                <span className="font-mono text-sm tracking-widest animate-pulse">Enviando...</span>
                            ) : (
                                <>
                                    <ActiveIcon className="w-4 h-4" />
                                    Enviar solicitud · {activeCarrier.label}
                                    <Send className="w-4 h-4" />
                                </>
                            )}
                        </motion.button>

                        <p className="text-xs font-mono tracking-widest text-slate-400 text-center uppercase">
                            Respuesta en menos de 24h · Sin compromiso
                        </p>
                    </motion.form>
                )}
            </div>
        </section>
    );
}

/* ── Field helpers ── */

type FieldProps = {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    name: string;
    type: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    required?: boolean;
    sub?: string;
};

function Field({ icon: Icon, label, name, type, value, onChange, placeholder, required, sub }: FieldProps) {
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={`field-${name}`} className="text-xs font-mono tracking-widest text-slate-500 uppercase flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" />
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <input
                id={`field-${name}`}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                className="w-full bg-white/70 border border-slate-200/60 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 transition-all text-sm"
            />
            {sub && <span className="text-[11px] text-slate-400 font-mono">{sub}</span>}
        </div>
    );
}

type SelectProps = {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: string[];
    placeholder: string;
    required?: boolean;
};

function SelectField({ icon: Icon, label, name, value, onChange, options, placeholder, required }: SelectProps) {
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={`select-${name}`} className="text-xs font-mono tracking-widest text-slate-500 uppercase flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" />
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <select
                id={`select-${name}`}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full bg-white/70 border border-slate-200/60 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 transition-all text-sm appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M6 8L1 3h10z' fill='%2394a3b8'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center' }}
            >
                <option value="">{placeholder}</option>
                {options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                ))}
            </select>
        </div>
    );
}
